import type { ReactNode } from 'react'

import FirebaseStorageImage from '@/components/molecules/firebase-storage-image'
import { cn } from '@/lib/utils'

type ImageThumbnailProps = {
  /** Firebase storage path. Provide this OR `src`. */
  storagePath?: string
  /** Direct image URL (e.g. a local blob preview). Used when `storagePath` is absent. */
  src?: string
  thumbhash?: string
  name?: string
  date?: string
  alt?: string
  selected?: boolean
  onClick?(): void
  /**
   * Top-right overlay rendered inside the thumbnail, e.g. a remove button or a
   * selection toggle. Use a `<span role="button">` (not `<button>`) since the
   * thumbnail itself is a button — nested native buttons are invalid.
   */
  overlay?: ReactNode
  className?: string
}

/**
 * Shared image thumbnail used by the gallery and the checkpoint dialogs so they
 * all share one look. Purely presentational — callers own click/preview/remove.
 */
const ImageThumbnail = ({
  storagePath,
  src,
  thumbhash,
  name,
  date,
  alt,
  selected,
  onClick,
  overlay,
  className,
}: ImageThumbnailProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex flex-col gap-1 text-left group rounded-sm',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
      className,
    )}
  >
    <div
      className={cn(
        'relative w-20 aspect-square rounded border border-subtle-2 overflow-hidden bg-layer-2 flex items-center justify-center',
        selected
          ? 'ring-2 ring-focus border-focus ring-offset-1'
          : 'hover:border-text-secondary/50',
      )}
    >
      {storagePath ? (
        <FirebaseStorageImage
          alt={alt ?? name ?? ''}
          className="object-cover size-full"
          storagePath={storagePath}
          thumbhash={thumbhash}
        />
      ) : (
        src && <img src={src} alt={alt ?? name ?? ''} className="object-cover size-full" />
      )}
      {overlay}
    </div>
    {name && <span className="text-[10px] text-text-secondary w-20 truncate">{name}</span>}
    {date && <span className="text-[10px] text-text-secondary">{date}</span>}
  </button>
)

export default ImageThumbnail
